/**
 * liveTutorCast — quel tuteur l'élève a devant lui, et les autres (Wajih, 20/10).
 *
 * Le roster d'une séance (`users`) porte tous les tuteurs de la salle : celui du jour, un tuteur en
 * observation, le titulaire d'un autre live de la même salle, le titulaire remplacé. L'ancien
 * live montrait toutes leurs caméras, la première arrivée en grand ; le nouveau prenait le premier
 * tuteur du roster (non trié) pour le nom et la première caméra d'un tuteur quelconque pour la
 * vidéo, et levait l'attente au premier tuteur connecté. Une seule règle, ici, que tout lit :
 * la scène, les pistes, la présence, l'attente, l'en-tête et le chat.
 *
 *  - **Le principal** est celui que le serveur dit (`today_tutor_id`), sinon le remplaçant de la séance du jour s'il y en a un (`substitute_id` de la
 *    plus proche ligne de `next_occurrences`, **finalisée comprise** : la même lecture que
 *    `LiveSession::getTodayOrNextOccurrenceTutorId` côté serveur, qui appelle
 *    `getTodayOrNextOccurrence()` sans écarter la séance finalisée), sinon le tuteur de la séance
 *    (`tutor`, son `tutor_id`). Sans l'un ni l'autre, le premier tuteur du roster.
 *  - **Présent** : un de ses appareils est dans la salle. Absent, sa place reste estompée et
 *    l'attente court : un autre tuteur connecté ne le remplace pas.
 *  - **Les autres tuteurs connectés** : en petits ronds, sans tandem ni place en grand ; leur partage
 *    d'écran ne prend pas la grande place, qui porterait le nom du principal (seul le principal y
 *    partage, décision du 20/10).
 *  - **Le même tuteur sur deux appareils** (`livePeople`) : une personne, et chaque caméra allumée
 *    se voit — celle à montrer en grand à sa place, les autres en ronds.
 */
import { type LivePerson } from "../room/livePeople"
import { todayOrNextOccurrence, type LiveOccurrenceLike } from "./liveSessionTime"

export type LiveCastSession = {
    /** Qui tient la séance du jour, dit par le serveur (`getTodayOrNextOccurrenceTutorId`). */
    today_tutor_id?: number | string | null
    tutor?: { id?: number | string | null } | null
    users?: { id?: number | string | null; is_tutor?: boolean | null }[] | null
    next_occurrences?: (LiveOccurrenceLike & { substitute_id?: number | string | null })[] | null
}

/** Un petit rond de la scène : un autre tuteur, ou une autre caméra du principal. */
export type LiveCastRound = {
    /** Unique sur la scène : l'identité de l'appareil pour une caméra du principal, l'id sinon. */
    key: string
    /** La personne qu'il montre. */
    personId: string
    /** L'appareil dont la caméra s'y montre ; `null` : sa photo. */
    camera: string | null
    cameraOn: boolean
    micOn: boolean
}

export type LiveTutorCast = {
    /** Le tuteur que l'élève attend, connecté ou non ; `null` : la séance n'en dit rien. */
    principalId: string | null
    /** Le principal, s'il est dans la salle. */
    principal: LivePerson | null
    /** Les autres tuteurs du roster connectés, dans l'ordre du roster. */
    others: LivePerson[]
    /** L'appareil dont la caméra prend la place du principal ; `null` : sa photo. */
    camera: string | null
    rounds: LiveCastRound[]
    /** Les appareils dont un partage d'écran se montre : ceux du principal seulement. */
    screens: string[]
}

const idOf = (value: number | string | null | undefined): string | null =>
    value != null && String(value) !== "" ? String(value) : null

/**
 * Les tuteurs du roster, dans son ordre, chacun une fois : un tuteur en double dans `users` donnait
 * deux ronds et deux puces de chat. La seule lecture des tuteurs du roster (la scène, le chat, la salle).
 */
export function rosterTutorIdsOf(session: LiveCastSession | null | undefined): string[] {
    const ids = (session?.users ?? [])
        .filter((member) => member?.is_tutor === true && idOf(member.id) != null)
        .map((member) => String(member.id))
    return [...new Set(ids)]
}

/** Le tuteur que l'élève a devant lui : le remplaçant du jour, sinon celui de la séance. */
export function principalTutorId(session: LiveCastSession | null | undefined): string | null {
    if (session == null) return null
    const today = todayOrNextOccurrence(session) as { substitute_id?: number | string | null } | null
    // Le serveur le dit d'abord : la copie d'un tuteur peut ne plus porter la séance du jour (un titulaire remplacé).
    return (
        idOf(session.today_tutor_id) ??
        idOf(today?.substitute_id) ??
        idOf(session.tutor?.id) ??
        rosterTutorIdsOf(session)[0] ??
        null
    )
}

export function liveTutorCast(
    session: LiveCastSession | null | undefined,
    people: readonly LivePerson[]
): LiveTutorCast {
    const principalId = principalTutorId(session)
    const remote = new Map(people.filter((person) => !person.isLocal).map((person) => [person.id, person]))
    const principal = principalId != null ? (remote.get(principalId) ?? null) : null
    const others = rosterTutorIdsOf(session)
        .filter((id) => id !== principalId)
        .map((id) => remote.get(id))
        .filter((person): person is LivePerson => person != null)

    const rounds: LiveCastRound[] = [
        ...(principal?.cameras.slice(1) ?? []).map((camera) => ({
            key: camera,
            personId: principal?.id ?? "",
            camera,
            cameraOn: true,
            micOn: principal?.micOn ?? false,
        })),
        ...others.map((person) => ({
            key: person.id,
            personId: person.id,
            camera: person.cameras[0] ?? null,
            cameraOn: person.cameraOn,
            micOn: person.micOn,
        })),
    ]
    return {
        principalId,
        principal,
        others,
        camera: principal?.cameras[0] ?? null,
        rounds,
        screens: principal?.identities ?? [],
    }
}
